import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLotSystem, useLotSystemMultiple, useLotSystemIntegration } from '../hooks/useLotSystem';

/**
 * Lot-System Context für globale Integration
 */
const LotSystemContext = createContext();

/**
 * Lot-System Provider für einheitliche Integration
 */
export const LotSystemProvider = ({ children }) => {
  const [globalCache, setGlobalCache] = useState(new Map());
  const [isInitialized, setIsInitialized] = useState(false);

  const clearGlobalCache = useCallback((productId = null) => {
    setGlobalCache((prev) => {
      if (productId == null) return new Map();
      if (!prev.has(productId)) return prev;
      const next = new Map(prev);
      next.delete(productId);
      return next;
    });
  }, []);

  // Global event handler
  useEffect(() => {
    const onDomainEvent = (event) => {
      if (event?.detail?.type !== 'inventory_updated') return;
      const detail = event?.detail?.payload || {};
      if (detail.productId) {
        clearGlobalCache(detail.productId);
      }
    };

    window.addEventListener('elkheir:domain-event', onDomainEvent);
    setIsInitialized(true);

    return () => {
      window.removeEventListener('elkheir:domain-event', onDomainEvent);
    };
  }, [clearGlobalCache]);

  const value = useMemo(
    () => ({
      isInitialized,
      globalCache,
      clearGlobalCache
    }),
    [isInitialized, globalCache, clearGlobalCache]
  );

  return (
    <LotSystemContext.Provider value={value}>
      {children}
    </LotSystemContext.Provider>
  );
};

/**
 * Hook für Lot-System Context
 */
export const useLotSystemContext = () => {
  const context = useContext(LotSystemContext);
  if (!context) {
    throw new Error('useLotSystemContext must be used within a LotSystemProvider');
  }
  return context;
};

/**
 * Einheitliche Lot-System Komponente
 * Stellt konsistente Lot-System Integration für alle Komponenten bereit
 */
export const LotSystemComponent = ({ 
  productId, 
  location = null, 
  children, 
  onUpdate,
  onError 
}) => {
  const lotSystem = useLotSystem(productId, location);
  const { isInitialized } = useLotSystemContext();

  // Error handling
  useEffect(() => {
    if (lotSystem.error && onError) {
      onError(lotSystem.error);
    }
  }, [lotSystem.error, onError]);

  // Update handling
  useEffect(() => {
    if (onUpdate) {
      onUpdate({
        productId,
        location,
        value: lotSystem.getValue(),
        quantity: lotSystem.getQuantity(),
        potentialProfit: lotSystem.getPotentialProfit(),
        lots: lotSystem.getLots()
      });
    }
  }, [productId, location, lotSystem, onUpdate]);

  const { t } = useLanguage();

  if (!isInitialized) {
    return <div>{t('stockPage.loadingLotSystem')}</div>;
  }

  return children({
    ...lotSystem,
    productId,
    location
  });
};

/**
 * Hook für einheitliche Lot-System Integration
 */
export const useUnifiedLotSystem = () => {
  const lotSystemIntegration = useLotSystemIntegration();
  const lotSystemContext = useLotSystemContext();

  return {
    ...lotSystemIntegration,
    ...lotSystemContext,
    
    // Utility functions
    isReady: lotSystemContext.isInitialized,
    
    // Batch operations
    batchUpdate: lotSystemIntegration.updateProducts,
    
    // Single operations
    update: lotSystemIntegration.updateProduct
  };
};
